import type { Container, PointData } from 'pixi.js';
import { Signal } from 'typed-signals';
import cloneDeep from 'lodash/cloneDeep';
import type { RecyclablePiece } from './RecyclableCullingView';
import { WinStreakChestLayout } from '../../../../layouts/winStreak/WinStreakChestLayout';
import type { WinStreakRoadChestData } from '../types';
import { createInventoryItemViews } from '../../../views/InventoryItemAmountView';
import { InventoryItem } from '../../../app/resources/types';
import { centerElements } from '../../../game/utils/centerElements';
import { FXParticleLayer } from '../../../libs/particles/FXParticleLayer';
import ParticlesConfig from '../../../../assets/particles{ignore}/Win_Streak_Reward_Particles.json';
import type { FXParticleEmitterConfig } from '../../../libs/particles/FXParticleEmitterConfig';
import { waitForMs } from '../../../utils/async-utils';
import { interpolationMap } from '../../../libs/TweenManager/interpolations';
import { RewardBubbleView } from '../../../views/RewardBubbleView';
import { SpeechBubbleSide } from '../../../views/SpeechBubbleView';
import {
  createLayoutAnimationPlayer,
  playLayoutAnimation,
} from '../../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { applyLayoutState } from '../../../libs/layouts/populateLayouts/applyLayoutState';
import type { LayoutAnimationPlayer } from '../../../libs/layouts/layoutAnimations/LayoutAnimationPlayer';

interface WinStreakChestViewConfig {
  root: Container;
  chestData: WinStreakRoadChestData;
  opened: boolean;
}

export class WinStreakChestView implements RecyclablePiece {
  public readonly onClicked = new Signal();

  private layout: WinStreakChestLayout | null = null;

  private bubbleReward: Container | null = null;

  private animationPlayer: LayoutAnimationPlayer | null = null;

  private opened = false;

  constructor(private readonly config: WinStreakChestViewConfig) {
    this.opened = config.opened;
  }

  public activate(): void {
    const {
      chestData: { x, y },
      root,
    } = this.config;

    this.layout = new WinStreakChestLayout();
    this.layout.root.props({
      parent: root,
      x,
      y,
    });

    this.layout.wrapper.scaleX = x < 0 ? 1 : -1;

    if (!this.opened) {
      this.animationPlayer = createLayoutAnimationPlayer(this.layout, 'Win_Streak_Chest_Idle');
      this.animationPlayer.play(true);

      this.layout.chestContainer.on('pointertap', () => {
        this.onClicked.emit();
      });
    } else {
      applyLayoutState(this.layout, 'opened');
    }
  }

  public turnOffBubbleReward(): void {
    if (!this.layout) return;
    this.bubbleReward?.destroy({ children: true });
    this.bubbleReward = null;
  }

  public toggleBubbleReward(): void {
    const {
      config: {
        chestData: { x, rewards },
      },
    } = this;

    if (!this.layout) return;
    if (!this.bubbleReward) {
      this.bubbleReward = new RewardBubbleView({
        pointer: { side: SpeechBubbleSide.Bottom, anchor: x < 0 ? 0 : 1 },
        rewards,
      }).props({
        parent: this.layout.rewardBubbleSlot,
      });
    } else {
      this.turnOffBubbleReward();
    }
  }

  public deactivate(): void {
    this.layout?.root.destroy({ children: true });
    this.layout = null;
    this.animationPlayer?.destroy();
    this.animationPlayer = null;
    this.bubbleReward = null;
  }

  public destroy(): void {
    this.deactivate();
  }

  public async open(): Promise<void> {
    if (!this.layout) return;

    this.opened = true;

    const {
      layout: { rewardSpawnPoint, root },
      config: {
        chestData: { rewards },
      },
    } = this;

    this.animationPlayer?.setPause(true);

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    const { promise, resolve } = Promise.withResolvers<void>();

    void playLayoutAnimation(this.layout, 'Win_Streak_Chest_Open', false, (eventName) => {
      if (eventName === 'spawnGifts') resolve();
    });

    await promise;

    const bubbles = createInventoryItemViews(rewards).map((view) =>
      view.props({
        parent: root.pixi.stage,
      }),
    );

    const startPoint = rewardSpawnPoint.getGlobalPosition();
    centerElements(bubbles, 20);
    bubbles.forEach((bubble) => {
      bubble.x += startPoint.x;
      bubble.y += startPoint.y;
      bubble.scale.set(0);
    });

    // make sure that bubbles are in view
    if (bubbles.length > 0 && bubbles[0].x < 100) {
      const offset = 100 - bubbles[0].x;
      bubbles.forEach((bubble) => {
        bubble.x += offset;
      });
    }

    await Promise.all(
      bubbles.map((bubble) => {
        bubble.tweenFrom({ x: startPoint.x, y: startPoint.y + 60 }, 400, 'cubicOut', true);
        return bubble.tweenTo({ scaleXY: 1 }, 400, 'cubicOut', true).asyncOnComplete();
      }),
    );

    await root.wait(400);

    const fxLayer = new FXParticleLayer(true).props({
      parent: root.pixi.stage,
    });

    await Promise.all(
      bubbles.map(async (bubble, index) => {
        const isCoin = bubble.getItemType() === InventoryItem.Coin;

        const delay = index * 100;
        const duration = 900 - delay;

        const target: PointData = isCoin
          ? { x: -100, y: 100 }
          : { x: root.pixi.renderer.width * 0.5, y: root.pixi.renderer.height + 100 };

        const tweenProps = isCoin
          ? {
              x: [bubble.x, target.x],
              y: [target.y, target.y],
            }
          : {
              x: [bubble.x > target.x ? bubble.x - 300 : bubble.x + 300, target.x],
              y: [bubble.y, target.y],
            };

        await bubble
          .tweenTo(tweenProps, duration, 'cubicIn', false, delay)
          .interpolation(interpolationMap.bezier)
          .start()
          .asyncOnComplete();

        const fxConfig = cloneDeep(ParticlesConfig as FXParticleEmitterConfig);
        if (bubble.getItemType() === InventoryItem.Coin) {
          fxConfig.emitAngle = 0;
        }

        fxLayer.addParticleEmitter(target.x, target.y, fxConfig);

        bubble.destroy({ children: true });
      }),
    );

    waitForMs(5000).then(
      () => {
        fxLayer.destroy();
      },
      () => {},
    );
  }
}
